import { useRef, useState } from 'react';

/**
 * SIGNATURE-RETOUR (08/11/2026) — zone de signature à la souris, au doigt ou au stylet (même principe que le portail terrain).
 *
 * Le dessin est fait sur un canevas TRANSPARENT (le fond clair et la ligne de signature sont du CSS) : un canevas resté vide donne un PNG sans
 * aucun pixel d'encre, que le serveur refuse (« n'a pas signé »). Rien n'est transmis tant que le trait est trop court pour être une signature.
 */
const LARGEUR = 640;
const HAUTEUR = 200;
const LONGUEUR_MINI = 140;

export const SignaturePad = ({ libelle, onChange }: { libelle: string; onChange: (png: string | null) => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dernier = useRef<{ x: number; y: number } | null>(null);
  const longueur = useRef(0);
  const [signe, setSigne] = useState(false);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * LARGEUR) / r.width, y: ((e.clientY - r.top) * HAUTEUR) / r.height };
  };
  const debut = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    canvasRef.current!.setPointerCapture(e.pointerId);
    dernier.current = point(e);
  };
  const trace = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dernier.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = point(e);
    ctx.strokeStyle = '#141B5B';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(dernier.current.x, dernier.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    longueur.current += Math.hypot(p.x - dernier.current.x, p.y - dernier.current.y);
    dernier.current = p;
  };
  const fin = () => {
    if (!dernier.current) return;
    dernier.current = null;
    if (longueur.current >= LONGUEUR_MINI) {
      setSigne(true);
      onChange(canvasRef.current!.toDataURL('image/png'));
    }
  };
  const effacer = () => {
    const c = canvasRef.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    longueur.current = 0;
    dernier.current = null;
    setSigne(false);
    onChange(null);
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{libelle}</label>
      <div style={{ position: 'relative', border: `2px solid ${signe ? 'var(--success, #15803d)' : '#cbd5e1'}`, borderRadius: 8, background: '#f8fafc', overflow: 'hidden' }}>
        {/* ligne de signature : décor CSS, jamais dessinée dans l'image */}
        <div aria-hidden="true" style={{ position: 'absolute', left: 16, right: 16, bottom: '28%', borderBottom: '2px dashed #cbd5e1', pointerEvents: 'none' }} />
        <canvas
          ref={canvasRef}
          width={LARGEUR}
          height={HAUTEUR}
          role="img"
          aria-label={`Zone de signature : ${libelle}`}
          onPointerDown={debut}
          onPointerMove={trace}
          onPointerUp={fin}
          onPointerCancel={fin}
          style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${LARGEUR} / ${HAUTEUR}`, touchAction: 'none', cursor: 'crosshair' }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
        <span>{signe ? 'Signature enregistrée' : 'Signez dans le cadre (souris, doigt ou stylet)'}</span>
        <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={effacer}>Effacer</button>
      </div>
    </div>
  );
};
